const exportErrors = {
    EXPORT_TIMEOUT: "Excel generation timed out. Try a smaller date range or fewer results.",
    EXPORT_RENDER_FAILED: "The server could not generate the Excel file. Please try again. If this continues, contact support.",
    EXPORT_INVALID_REQUEST: "The export data is invalid. Refresh the report and try again.",
    EXPORT_FAILED: "The export failed on the server. Please try again. If this continues, contact support."
};
async function readErrorBody(data) {
    try {
        // Axios keeps JSON error responses as blobs because the request downloads a file.
        if (typeof Blob !== "undefined" && data instanceof Blob) {
            if (data.size > 65536)
                return;
            data = await data.text();
        }
        if (typeof data === "string") {
            if (data.length > 65536)
                return;
            data = JSON.parse(data);
        }
        if (data && typeof data === "object" && !Array.isArray(data)) {
            return data;
        }
    }
    catch {
        // HTML proxy errors, malformed JSON, and unreadable blobs use the HTTP fallback.
    }
}
export async function getExportErrorMessage(error, stage = "request") {
    const status = error?.response?.status;
    const body = await readErrorBody(error?.response?.data);
    const code = body?.code;
    let message;
    if (typeof code === "string" && Object.prototype.hasOwnProperty.call(exportErrors, code)) {
        message = exportErrors[code];
    }
    else if (error?.code === "EXPORT_INVALID_RESPONSE") {
        message = "The server returned an unexpected response instead of an Excel file. Refresh the page and try again.";
    }
    else if (stage === "preparing") {
        message = "The report data could not be prepared for export. Refresh the report and try again.";
    }
    else if (stage === "download") {
        message = "The Excel file was created, but the browser could not start the download. Please try again.";
    }
    else if (error?.code === "ETIMEDOUT" ||
        (error?.code === "ECONNABORTED" && /timeout/i.test(error?.message || "")) ||
        status === 408 || status === 504) {
        message = "The export request timed out. Try a smaller date range or fewer results.";
    }
    else if (status === 401 || status === 302) {
        message = "Please sign in again to export this report.";
    }
    else if (status === 403) {
        message = "The server denied access to this export. Contact support if you need access.";
    }
    else if (status === 413) {
        message = "This export is too large for the server. Try a smaller date range or fewer results.";
    }
    else if (status === 400 || status === 422) {
        message = "The export request was invalid. Refresh the report and try again.";
    }
    else if (status === 429) {
        message = "Too many requests. Wait a moment and try exporting again.";
    }
    else if (status === 502 || status === 503) {
        message = "The export service is unavailable. Please try again shortly.";
    }
    else if (error?.code === "ERR_CANCELED" || error?.__CANCEL__) {
        message = "The export was canceled. Please try again when you are ready.";
    }
    else if (error?.code === "ECONNABORTED") {
        message = "The export request was interrupted. Please try again.";
    }
    else if (!error?.response && (error?.isAxiosError || error?.code === "ERR_NETWORK")) {
        message = "The export connection failed. Check your connection and try again.";
    }
    else {
        message = "The export failed. Please try again. If this continues, contact support.";
    }
    // Never display server exception text/HTML: renderer errors may contain report data.
    const traceId = body?.traceId;
    if (typeof traceId === "string" && /^[A-Za-z0-9:._-]{1,100}$/.test(traceId)) {
        message += ` Reference: ${traceId}`;
    }
    return message;
}
export function isUnexpectedExportResponse(response) {
    const contentType = response?.headers?.["content-type"] || response?.data?.type || "";
    return /(?:text\/|(?:application\/[^;]*json))/i.test(contentType);
}
